import { ADVIEZEN, type Advies, type TuinAdvies } from '@/types/domain';
import { Getalveld, JaNee, Meerkeuzeveld, Tekstvak } from '@/components/ui/Velden';
import './form.css';

interface TuinVeldenProps {
  /** "Voortuin" of "Achtertuin"; alleen voor labels en toegankelijkheid. */
  naam: string;
  waarde: TuinAdvies;
  /**
   * Krijgt een functie mee in plaats van een waarde. React bundelt
   * wijzigingen die vlak na elkaar komen; met een vaste waarde zou een
   * tweede tik werken op een verouderde tuin en de eerste wissen.
   */
  bijWijziging: (bijwerken: (huidig: TuinAdvies) => TuinAdvies) => void;
}

/**
 * Voor- en achtertuin hebben exact hetzelfde veldenpakket, dus één component
 * bedient beide. Zo blijven de twee secties gegarandeerd in de pas lopen.
 */
export function TuinVelden({ naam, waarde, bijWijziging }: TuinVeldenProps): JSX.Element {
  const zet = <K extends keyof TuinAdvies>(veld: K, nieuweWaarde: TuinAdvies[K]) => {
    bijWijziging((huidig) => ({ ...huidig, [veld]: nieuweWaarde }));
  };

  return (
    <>
      <JaNee
        label={`${naam} aanwezig`}
        waarde={waarde.aanwezig}
        bijWijziging={(aanwezig) => {
          // Niet aanwezig: advies en berging leegmaken zodat er geen
          // restwaarden blijven staan van een eerdere invulling.
          if (!aanwezig) {
            bijWijziging((huidig) => ({
              ...huidig,
              aanwezig: false,
              advies: [],
              bergingM3: null,
            }));
            return;
          }
          zet('aanwezig', true);
        }}
      />

      {waarde.aanwezig && (
        <>
          <Meerkeuzeveld<Advies>
            label={`Advies ${naam.toLowerCase()}`}
            waarden={waarde.advies}
            opties={ADVIEZEN}
            bijWijziging={(bijwerken) =>
              bijWijziging((huidig) => ({ ...huidig, advies: bijwerken(huidig.advies) }))
            }
            hint="Meerdere adviezen mogen tegelijk."
          />

          <Getalveld
            label="Indicatieve berging"
            waarde={waarde.bergingM3}
            bijWijziging={(berging) => zet('bergingM3', berging)}
            min={0}
            max={500}
            stap={0.25}
            eenheid="m³"
            hint="Tik met de knoppen per kwart kuub, of typ zelf een ander getal."
          />
        </>
      )}

      <Tekstvak
        label="Toelichting"
        lezers="gemeente"
        waarde={waarde.toelichting}
        bijWijziging={(toelichting) => zet('toelichting', toelichting)}
        placeholder={`Bijzonderheden over de ${naam.toLowerCase()}`}
      />
    </>
  );
}

/**
 * Of er in deze tuin al een keuze is gemaakt.
 *
 * "Niet aanwezig" telt mee als ingevuld: dat is een antwoord, geen leegte.
 * Een tuin staat standaard op aanwezig, dus zonder advies of kubieke meters
 * is er nog niets vastgelegd.
 */
export function tuinIngevuld(tuin: TuinAdvies): boolean {
  return !tuin.aanwezig || tuin.advies.length > 0 || Boolean(tuin.bergingM3);
}

/**
 * Wat er in deze tuin staat, in één regel voor de sectiekop.
 *
 * Zo hoeft een coach de sectie niet open te klappen om te weten of hij er nog
 * moet zijn.
 */
export function tuinSamenvatting(tuin: TuinAdvies): string | undefined {
  if (!tuin.aanwezig) return 'Niet aanwezig';

  const delen: string[] = [];
  if (tuin.advies.length > 0) {
    delen.push(tuin.advies.length === 1 ? '1 advies' : `${tuin.advies.length} adviezen`);
  }
  if (tuin.bergingM3) delen.push(`${tuin.bergingM3} m³`);

  return delen.length > 0 ? `Aanwezig · ${delen.join(' · ')}` : undefined;
}
